import { createAction } from 'redux-actions';
import { isoFormat } from 'd3-time-format';
import { timeMinute, timeMonth } from 'd3-time';

import {
  descriptionCreator,
  idCreator,
  metaCreator,
  payloadCreator,
} from './creator';
import { checkForCachedDataAction, requestAction } from './request';
import fetch, {
  Api,
  defaultRequestConfig,
  getExtendedRequestConfig,
  getJson,
  handleError,
} from './fetch';
import activityTypesGoogle from '../utils/activityTypesGoogle.json';

const fitTimeParse = t => new Date(+t);
const fitTimeFormat = isoFormat;

const stepsQuantizeDuration = 300000;

export const fitFetchAction = createAction('GET_GOOGLE_FIT_DATA', payloadCreator, metaCreator);

const parseFitSessions = (data) => {
  const exercises = [];
  const nutrition = [];
  const sleep = [];
  const notes = [];
  const { session } = data;
  if (session) {
    session.forEach((s) => {
      const startTime = fitTimeParse(s.startTimeMillis);
      const endTime = fitTimeParse(s.endTimeMillis);
      const item = {
        id: s.id,
        time: startTime.toISOString(),
        endTime: endTime.toISOString(),
        raw: s,
        source: 'google',
        type: activityTypesGoogle[s.activityType],
      };
      item.duration = s.duration || timeMinute.count(startTime, endTime);
      if (s.name) {
        item.exercise = s.name;
      }
      switch (item.type) {
        case 'sleeping':
        case 'deep sleep':
        case 'light sleep':
        case 'rem sleep':
          sleep.push(item);
          break;
        default:
          exercises.push(item);
      }
    });
  }
  return {
    exercises,
    nutrition,
    sleep,
    notes,
  };
};

const getDataSourceURL = dataSource => `${Api}/google/users/me/dataSources/${
  encodeURIComponent(dataSource.dataStreamId)
}/dataPointChanges`;

const parseBloodPressure = (json) => {
  return json.insertedDataPoint.map(dp => ({
    time: new Date(dp.startTimeNanos / 1000000).toISOString(),
    systolic: dp.value[0].fpVal,
    diastolic: dp.value[1].fpVal,
  }));
}

const parseWeight = (json) => {
  // TODO: weight values are in funny floating point precisions
  // We could tidy them up here...
  return json.insertedDataPoint.map(dp => ({
    time: new Date(dp.startTimeNanos / 1000000).toISOString(),
    weight: dp.value[0].fpVal,
  }));
}

const dataSourceStreams = [
  {
    name: 'com.google.blood_pressure',
    type: 'bloodPressure',
    parser: parseBloodPressure,
  },
  {
    name: 'com.google.weight',
    type: 'weight',
    parser: parseWeight,
  }
]

const parseFitDataSources = (data) => {
  const sources = [];
  const { dataSource } = data;
  dataSource.forEach(source => {
    if (!source.dataType) {
      return;
    }
    dataSourceStreams.some((stream) => {
      if (source.dataType.name === stream.name) {
        if (source.type === 'derived') {
          const sourceUrl = getDataSourceURL(source);
          sources.push({
            ...stream,
            sourceUrl,
          });
          return true;
        }
      }
      return false;
    });
  });
  return sources;
};

const parseFitDataSourceData = ({ stream, params, dispatch }) => {
  const id = idCreator();
  const description = descriptionCreator({ source: 'Google Fit', type: stream.type, params });
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => fetch(stream.sourceUrl, defaultRequestConfig))
    .then(handleError)
    .then(getJson)
    .then(stream.parser)
    .then(data => dispatch(fitFetchAction({ id, description, data: { [stream.type]: data } })))
    .catch(error => dispatch(fitFetchAction({ id, description, error: error.message || error })));
};

const getSessions = ({ params }) => {
  const startDateString = (params.startTime)
    ? `?startTime=${fitTimeFormat(params.startTime)}`
    : '';
  const endDateParamSeparator = startDateString.length
    ? '&'
    : '?';
  const endDateString = (params.endTime)
    ? `${endDateParamSeparator}endTime=${fitTimeFormat(params.endTime)}`
    : '';
  return `${Api}/google/users/me/sessions${
    startDateString
  }${
    endDateString
  }`;
};

const getFitSessionData = ({ params = defaultParams, dispatch }) => {
  const id = idCreator();
  const description = descriptionCreator({ source: 'Google Fit', type: 'Session Data', params });
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => fetch(getSessions({ params }), defaultRequestConfig))
    .then(handleError)
    .then(getJson)
    .then(json => parseFitSessions(json))
    .then(data => dispatch(fitFetchAction({ id, description, data })))
    .catch(error => dispatch(fitFetchAction({ id, description, error: error.message || error })));
};

const getFitStepData = ({ params = defaultParams, dispatch }) => {
  const id = idCreator();
  const description = descriptionCreator({ source: 'Google Fit', type: 'Step Counts', params });

  const stepsUrl = `${Api}/google/users/me/dataset:aggregate?fields=${
    encodeURIComponent('bucket/startTimeMillis,bucket/dataset/point/value/intVal')}`;

  const aggregateParams = {
    aggregateBy: [{
      dataTypeName: 'com.google.step_count.delta',
      dataSourceId: 'derived:com.google.step_count.delta:com.google.android.gms:estimated_steps',
    }],
    bucketByTime: { durationMillis: stepsQuantizeDuration },
    startTimeMillis: params.startTime.getTime(),
    endTimeMillis: params.endTime.getTime(),
  };
  const postParams = getExtendedRequestConfig({
    params: {
      method: 'POST',
      body: aggregateParams,
    },
  });

  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => fetch(stepsUrl, postParams))
    .then(handleError)
    .then(getJson)
    .then((data) => {
      const entries = (data.bucket || []).reduce((arr, e, i) => {
        const steps = (e.dataset || []).reduce((count, d, j) => {
          (d.point || []).forEach((p) => {
            (p.value || []).forEach((v) => {
              count += v.intVal || 0;
            })
          });
          return count;
        }, 0);
        if (steps) {
          try {
            const entry = {
              time: new Date(+e.startTimeMillis).toISOString(),
              endTime: new Date(+e.startTimeMillis + stepsQuantizeDuration).toISOString(),
              steps,
            };
            arr.push(entry);
          } catch (error) {
            dispatch(fitFetchAction({ id, description, error: error.message || error }));
          }
        }
        return arr;
      }, []);
      return ({
        steps: entries,
      })
    })
    .then(data => dispatch(fitFetchAction({ id, description, data })))
    .catch(error => dispatch(fitFetchAction({ id, description, error: error.message || error })));
};

const getFitContinuousData = ({ params = defaultParams, dispatch }) => {
  const id = idCreator();
  const description = descriptionCreator({ source: 'Google Fit', type: 'Continuous Data', params });
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
  .then(() => fetch(`${Api}/google/users/me/dataSources`, defaultRequestConfig))
    .then(handleError)
    .then(getJson)
    .then(parseFitDataSources)
    .then((streams) => {
      const promises = streams.map(stream => parseFitDataSourceData({ stream, params, dispatch }));
      return Promise.all(promises);
    })
    .then(() => dispatch(fitFetchAction({ id, description, data: {} })))
    .catch(error => dispatch(fitFetchAction({ id, description, error: error.message || error })));
};

const defaultParams = {
  startTime: timeMonth(new Date()),
  endTime: new Date(),
};

export const getFitData = ({ params = defaultParams }) => ((dispatch) => {
  const sessionPromise = getFitSessionData({ params, dispatch });
  const continuousDataPromise = getFitContinuousData({ params, dispatch });
  const stepDataPromise = getFitStepData({ params, dispatch });
  return Promise.all([sessionPromise, continuousDataPromise, stepDataPromise]);
});

export default getFitData;
